import React, { useEffect } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { getFormEnginePortalUrlWithAuth } from '../../utils/formEnginePortal.utils';
import { LoadingSpinner } from '../Loading/LoadingSpinner';

/**
 * Redirects to Form Engine Portal for form and exam form routes.
 * Passes the current auth token in the URL hash so FormEnginePortal can call APIs without re-auth.
 * HCP keeps these paths so bookmarks and nav links continue to work; rendering happens in FEP.
 */
export const FormEngineRedirect: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const query = searchParams.toString();
      const queryPart = query ? `?${query}` : '';
      let path: string;
      if (pathname.startsWith('/form/') && formId) {
        path = `/form/${formId}${queryPart}`;
      } else if (pathname === '/examform') {
        path = `/examform${queryPart}`;
      } else if (pathname === '/view') {
        const viewFormId = searchParams.get('formId') || searchParams.get('documentId') || '1';
        path = `/form/${encodeURIComponent(viewFormId)}${queryPart}`;
      } else {
        path = `/form/1${queryPart}`;
      }
      const url = await getFormEnginePortalUrlWithAuth(path);
      if (!cancelled) {
        window.location.href = url;
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [pathname, formId, searchParams]);

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center px-8 py-8">
      <LoadingSpinner
        layout="embedded"
        message="Redirecting to Form Engine…"
        ariaLabel="Redirecting to Form Engine"
      />
    </div>
  );
};
